import { PageHeader } from '@/shared/components/ui/PageHeader';
import { Briefcase } from 'lucide-react';
import { useMemo, useState, type ChangeEvent } from 'react';
import { generatePath, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { BriefcaseBusiness, Plus, UserPlus, Check, HelpCircle, X, MoreHorizontal, Search } from 'lucide-react';
import { Badge, type BadgeTone } from '@/shared/components/ui/Badge';
import { Button } from '@/shared/components/ui/Button';
import { Card, CardBody, CardHeader } from '@/shared/components/ui/Card';
import { EmptyState } from '@/shared/components/ui/EmptyState';
import { Input } from '@/shared/components/ui/Input';
import { Modal } from '@/shared/components/ui/Modal';
import { Select } from '@/shared/components/ui/Select';
import { Spinner } from '@/shared/components/ui/Spinner';
import { EmailSendModal } from '@/shared/components/ui/EmailSendModal';
import { useUser } from '@/shared/hooks/useAuth';
import { ROUTES } from '@/routes/paths';
import { getApiErrorMessage } from '@/shared/api/axios';
import { useJobApplications, useJobPostings, useRecruitmentMutations, useMasterDepartments } from '../api';
import { JobPostingsTab, EditJobModal } from '../components/JobPostingsTab';
import { ScheduleInterviewModal } from '../components/ScheduleInterviewModal';
import type { ApplicationStage, EmploymentType, JobApplication, JobPosting } from '../types';

const MAX_RESUME_BYTES = 10 * 1024 * 1024; // keep in sync with the server limit

// ─── Constants ──────────────────────────────────────────────────────────────────

const STAGE_LABELS: Record<ApplicationStage, string> = {
  applied: 'Applied',
  ai_screened: 'AI Screened',
  contacted: 'Contacted',
  interview: 'Interview',
  hired: 'Hired',
  rejected: 'Rejected',
};

const STAGE_TONES: Record<ApplicationStage, BadgeTone> = {
  applied: 'info',
  ai_screened: 'warning',
  contacted: 'primary',
  interview: 'info',
  hired: 'success',
  rejected: 'danger',
};

const STAGE_ORDER: ApplicationStage[] = ['applied', 'ai_screened', 'contacted', 'interview', 'hired', 'rejected'];

const NEXT_STAGE: Partial<Record<ApplicationStage, ApplicationStage>> = {
  applied: 'ai_screened',
  ai_screened: 'contacted',
  contacted: 'interview',
  interview: 'hired',
};

const EMPLOYMENT_OPTIONS: { label: string; value: EmploymentType }[] = [
  { label: 'Full Time', value: 'full_time' },
  { label: 'Part Time', value: 'part_time' },
  { label: 'Contract', value: 'contract' },
  { label: 'Intern', value: 'intern' },
];

// ─── Helpers ────────────────────────────────────────────────────────────────────

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  return `${months}mo ago`;
}

// ─── Main Page ──────────────────────────────────────────────────────────────────

export function RecruitmentPage() {
  const user = useUser();
  const { data: jobs, isLoading: jobsLoading } = useJobPostings();
  const { createJob, updateJob, addApplication, moveStage } = useRecruitmentMutations();

  const [activeTab, setActiveTab] = useState<'postings' | 'applications'>('postings');
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);
  const [stageFilter, setStageFilter] = useState<ApplicationStage | 'all'>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<'newest' | 'oldest' | 'name'>('newest');

  const navigate = useNavigate();
  const [showNewJobModal, setShowNewJobModal] = useState(false);
  const [showNewCandidateModal, setShowNewCandidateModal] = useState(false);
  const [editingJob, setEditingJob] = useState<JobPosting | null>(null);
  const [rejectionTarget, setRejectionTarget] = useState<JobApplication | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [interviewTarget, setInterviewTarget] = useState<JobApplication | null>(null);
  const [emailDraft, setEmailDraft] = useState<{ to: string; subject: string; html: string } | null>(null);

  function openApplication(app: JobApplication) {
    navigate(generatePath(ROUTES.recruitmentApplication, { applicationId: app._id }));
  }

  // Default to all jobs (no filter) when no specific job selected
  const activeJobId = selectedJobId ?? '';
  const { data: applications, isLoading: appsLoading } = useJobApplications(activeJobId || null);

  const activeJob = useMemo(() => (activeJobId ? jobs?.find((j) => j._id === activeJobId) : null) ?? null, [jobs, activeJobId]);

  const canManage =
    user?.role === 'superadmin' ||
    user?.role === 'admin' ||
    (user?.department === 'hr' && (user?.role === 'manager' || user?.role === 'team_lead'));

  const canCreateJob = canManage;

  // Filter + sort applications
  const filteredApps = useMemo(() => {
    let list = applications ?? [];
    if (stageFilter !== 'all') {
      list = list.filter((a) => a.stage === stageFilter);
    }
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      list = list.filter(
        (a) => a.candidateName.toLowerCase().includes(term) || a.email.toLowerCase().includes(term),
      );
    }
    list = [...list].sort((a, b) => {
      if (sortBy === 'newest') return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      if (sortBy === 'oldest') return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
      return a.candidateName.localeCompare(b.candidateName);
    });
    return list;
  }, [applications, stageFilter, searchTerm, sortBy]);

  // Stage counts from currently loaded applications
  const stageCounts = useMemo(() => {
    const counts: Record<string, number> = { all: 0 };
    for (const stage of STAGE_ORDER) counts[stage] = 0;
    for (const app of applications ?? []) {
      counts[app.stage] = (counts[app.stage] ?? 0) + 1;
      counts.all += 1;
    }
    return counts;
  }, [applications]);

  // ─── Handlers ───────────────────────────────────────────────────────────────

  function handleAdvance(app: JobApplication) {
    const next = NEXT_STAGE[app.stage];
    if (!next) return;
    // Moving to 'interview' needs a date/time first — the backend only books
    // a Meeting and drafts the invite email when interviewAt is supplied.
    if (next === 'interview') {
      setInterviewTarget(app);
      return;
    }
    moveStage.mutate(
      { id: app._id, stage: next },
      {
        onSuccess: (result) => {
          if (next === 'hired') {
            toast.success(`Moved ${app.candidateName} to Hired — a draft offer letter has been created. Complete it on the Offer Letters page.`, { duration: 6000 });
            if (result?.onboardingLink) toast.success(`Onboarding link: ${result.onboardingLink}`, { duration: 8000 });
          } else {
            toast.success(`Moved ${app.candidateName} to ${STAGE_LABELS[next]}`);
          }
        },
      },
    );
  }

  function confirmScheduleInterview(interviewAt: string, note: string) {
    if (!interviewTarget) return;
    moveStage.mutate(
      { id: interviewTarget._id, stage: 'interview', interviewAt, note: note || undefined },
      {
        onSuccess: (result) => {
          toast.success(`Interview scheduled for ${interviewTarget.candidateName}`);
          setInterviewTarget(null);
          if (result?.emailDraft) setEmailDraft(result.emailDraft);
        },
        onError: () => {
          toast.error('Could not schedule the interview.');
        },
      },
    );
  }

  function handleReject(app: JobApplication) {
    setRejectionTarget(app);
    setRejectionReason('');
  }

  function confirmReject() {
    if (!rejectionTarget) return;
    moveStage.mutate(
      { id: rejectionTarget._id, stage: 'rejected', rejectionReason },
      {
        onSuccess: () => {
          toast.success(`${rejectionTarget.candidateName} moved to Rejected`);
          setRejectionTarget(null);
        },
      },
    );
  }

  // ─── Loading state ──────────────────────────────────────────────────────────

  if (jobsLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Spinner />
      </div>
    );
  }

  // ─── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="flex flex-col gap-6 pb-16">
      {/* Header */}
      <PageHeader icon={Briefcase} eyebrow="People" title="Recruitment" description="Job postings and the applications they receive." actions={<div className="flex gap-2">
          {activeTab === 'applications' && canManage && (
            <Button variant="outline" size="sm" onClick={() => setShowNewCandidateModal(true)}>
              <UserPlus className="h-4 w-4" />
              Add Candidate
            </Button>
          )}
          {canCreateJob && (
            <Button size="sm" onClick={() => setShowNewJobModal(true)}>
              <Plus className="h-4 w-4" />
              New Job Posting
            </Button>
          )}
        </div>} />

      {/* Top-level Tab Switcher */}
      <div className="flex gap-1 border-b border-outline">
        <button
          onClick={() => setActiveTab('postings')}
          className={`border-b-2 px-5 py-2.5 text-sm font-semibold transition-colors ${
            activeTab === 'postings' ? 'border-primary text-primary' : 'border-transparent text-on-surface-variant hover:text-on-surface'
          }`}
        >
          Job Postings
        </button>
        <button
          onClick={() => setActiveTab('applications')}
          className={`border-b-2 px-5 py-2.5 text-sm font-semibold transition-colors ${
            activeTab === 'applications' ? 'border-primary text-primary' : 'border-transparent text-on-surface-variant hover:text-on-surface'
          }`}
        >
          Applications
        </button>
      </div>

      {/* ═══ Job Postings Tab ═══════════════════════════════════════════════════ */}
      {activeTab === 'postings' && (
        <JobPostingsTab
          jobs={jobs ?? []}
          applications={applications ?? []}
          canManage={canManage}
          onEdit={setEditingJob}
          onToggleStatus={(job) => {
            const next = job.status === 'open' ? 'closed' : 'open';
            updateJob.mutate(
              { id: job._id, status: next },
              { onSuccess: () => toast.success(`${job.title} is now ${next}.`) },
            );
          }}
          updateLoading={updateJob.isPending}
        />
      )}

      {/* ═══ Applications Tab ═════════════════════════════════════════════════════ */}
      {activeTab === 'applications' && (
        <>
      {/* Stage Filter Tabs */}
      <div className="flex gap-1 overflow-x-auto border-b border-outline">
        {(['all', ...STAGE_ORDER] as const).map((stage) => {
          const label = stage === 'all' ? 'All applications' : STAGE_LABELS[stage];
          const count = stageCounts[stage] ?? 0;
          const isActive = stageFilter === stage;
          return (
            <button
              key={stage}
              onClick={() => setStageFilter(stage)}
              className={`shrink-0 border-b-2 px-4 py-2 text-sm font-medium transition-colors ${
                isActive
                  ? 'border-primary text-primary'
                  : 'border-transparent text-on-surface-variant hover:text-on-surface'
              }`}
            >
              {label} &middot; {count}
            </button>
          );
        })}
      </div>

      {/* Sort/Filter/Search */}
      <div className="flex items-center gap-3">
        <div className="min-w-[320px] flex-[3]">
          <Input
            placeholder="Search candidates by name or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            leftIcon={<Search className="h-4 w-4" />}
          />
        </div>
        <div className="w-56 shrink-0">
          <Select
            options={[{ label: 'All Job Roles', value: '' }, ...(jobs ?? []).map((j) => ({ label: j.title, value: j._id }))]}
            value={activeJobId}
            onChange={(e) => setSelectedJobId(e.target.value || null)}
            aria-label="Filter by role"
          />
        </div>
        <div className="w-44 shrink-0">
          <Select
            options={[
              { label: 'Newest first', value: 'newest' },
              { label: 'Oldest first', value: 'oldest' },
              { label: 'Name A-Z', value: 'name' },
            ]}
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as 'newest' | 'oldest' | 'name')}
            aria-label="Sort by"
          />
        </div>
      </div>

      {/* Table */}
      <Card>
        <CardHeader
          title={`${filteredApps.length} candidate${filteredApps.length !== 1 ? 's' : ''}`}
        />
        <CardBody>
          {appsLoading ? (
            <div className="flex justify-center py-8">
              <Spinner />
            </div>
          ) : filteredApps.length === 0 ? (
            <EmptyState
              icon={<BriefcaseBusiness className="h-6 w-6" />}
              title="No candidates found"
              description="Try adjusting your filters or add a new candidate."
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-b border-outline text-on-surface-variant">
                    <th className="w-8 px-3 py-3">
                      <input type="checkbox" className="rounded border-outline" aria-label="Select all" />
                    </th>
                    <th className="px-3 py-3">Candidates</th>
                    <th className="px-3 py-3">Matches to job post</th>
                    <th className="px-3 py-3">Rejection Reason</th>
                    <th className="px-3 py-3">Activity</th>
                    <th className="px-3 py-3">Interest</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredApps.map((app) => (
                    <ApplicationRow
                      key={app._id}
                      app={app}
                      job={activeJob}
                      canManage={canManage}
                      onAdvance={() => handleAdvance(app)}
                      onReject={() => handleReject(app)}
                      onReview={() => openApplication(app)}
                      onRowClick={() => openApplication(app)}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>
        </>
      )}

      {/* Modals (shared — outside tab fragments) */}
      {showNewJobModal && (
        <NewJobModal
          open={showNewJobModal}
          onClose={() => setShowNewJobModal(false)}
          onCreate={(payload) => {
            createJob.mutate({ ...payload, status: 'open' }, {
              onSuccess: () => {
                toast.success('Job posting created');
                setShowNewJobModal(false);
              },
            });
          }}
          loading={createJob.isPending}
        />
      )}

      {showNewCandidateModal && (
        <NewCandidateModal
          open={showNewCandidateModal}
          onClose={() => setShowNewCandidateModal(false)}
          jobs={jobs ?? []}
          defaultJobId={activeJobId || null}
          onAdd={({ jobId, ...payload }) => {
            addApplication.mutate(
              { jobId, ...payload },
              {
                onSuccess: () => {
                  toast.success('Candidate added');
                  setShowNewCandidateModal(false);
                },
                onError: (err) => toast.error(getApiErrorMessage(err, 'Could not add candidate.')),
              },
            );
          }}
          loading={addApplication.isPending}
        />
      )}

      {/* Rejection Reason Modal */}
      <Modal
        open={!!rejectionTarget}
        onClose={() => setRejectionTarget(null)}
        title="Reject Candidate"
        size="sm"
        footer={
          <div className="flex gap-2">
            <Button variant="ghost" onClick={() => setRejectionTarget(null)}>
              Cancel
            </Button>
            <Button variant="danger" onClick={confirmReject} loading={moveStage.isPending}>
              Reject
            </Button>
          </div>
        }
      >
        <p className="mb-3 text-sm text-on-surface-variant">
          Provide a reason for rejecting <strong>{rejectionTarget?.candidateName}</strong>.
        </p>
        <Input
          label="Rejection reason"
          value={rejectionReason}
          onChange={(e) => setRejectionReason(e.target.value)}
          placeholder="e.g. Not enough experience"
        />
      </Modal>

      {/* Schedule Interview Modal */}
      {interviewTarget && (
        <ScheduleInterviewModal
          open={!!interviewTarget}
          candidateName={interviewTarget.candidateName}
          loading={moveStage.isPending}
          onClose={() => setInterviewTarget(null)}
          onConfirm={confirmScheduleInterview}
        />
      )}

      {/* Interview invite email draft */}
      {emailDraft && (
        <EmailSendModal
          open={!!emailDraft}
          onClose={() => setEmailDraft(null)}
          initialTo={emailDraft.to}
          initialSubject={emailDraft.subject}
          initialHtml={emailDraft.html}
          onSent={() => setEmailDraft(null)}
        />
      )}

      {/* Edit Job Modal */}
      {editingJob && (
        <EditJobModal
          open={!!editingJob}
          job={editingJob}
          onClose={() => setEditingJob(null)}
          onSave={(id, payload) => {
            updateJob.mutate(
              { id, ...payload },
              {
                onSuccess: () => {
                  toast.success('Job posting updated');
                  setEditingJob(null);
                },
                onError: (err) => toast.error(getApiErrorMessage(err, 'Could not update posting.')),
              },
            );
          }}
          loading={updateJob.isPending}
        />
      )}
    </div>
  );
}

// ─── Application Row ────────────────────────────────────────────────────────────

interface ApplicationRowProps {
  app: JobApplication;
  job: JobPosting | null;
  canManage: boolean;
  onAdvance: () => void;
  onReject: () => void;
  onReview: () => void;
  onRowClick: () => void;
}

function ApplicationRow({ app, job, canManage, onAdvance, onReject, onReview, onRowClick }: ApplicationRowProps) {
  const matchedSkills = app.screeningMatchedSkills ?? [];
  const requiredSkills = job?.requiredSkills ?? [];

  return (
    <tr
      className="cursor-pointer border-b border-outline/50 transition-colors hover:bg-surface-variant/50"
      onClick={onRowClick}
    >
      <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
        <input type="checkbox" className="rounded border-outline" aria-label={`Select ${app.candidateName}`} />
      </td>
      <td className="px-3 py-3">
        <div className="flex flex-col gap-1">
          <span className="font-semibold text-on-surface">{app.candidateName}</span>
          <span className="text-xs text-on-surface-variant">
            {job?.title ?? 'Unknown role'} &middot; {job?.location ?? ''}
          </span>
          <div className="flex items-center gap-2">
            <Badge tone={STAGE_TONES[app.stage]}>{STAGE_LABELS[app.stage]}</Badge>
            <span className="text-xs text-on-surface-variant">Applied {timeAgo(app.createdAt)}</span>
          </div>
        </div>
      </td>
      <td className="px-3 py-3">
        <div className="flex flex-wrap gap-1">
          {requiredSkills.map((skill) => {
            const matched = matchedSkills.includes(skill);
            return (
              <Badge key={skill} tone={matched ? 'success' : 'danger'} className="text-xs">
                {matched ? '✓' : '✗'} {skill}
              </Badge>
            );
          })}
        </div>
      </td>
      <td className="px-3 py-3 max-w-[220px]">
        {app.stage === 'rejected' && app.rejectionReason ? (
          <span className="text-xs text-danger-text" title={app.rejectionReason}>
            {app.rejectionReason}
          </span>
        ) : (
          <span className="text-xs text-on-surface-variant">—</span>
        )}
      </td>
      <td className="px-3 py-3">
        <span className="text-xs text-on-surface-variant">{timeAgo(app.updatedAt)}</span>
      </td>
      <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-1">
          {canManage && app.stage !== 'hired' && app.stage !== 'rejected' && (
            <button
              onClick={onAdvance}
              className="rounded p-1.5 text-success-text hover:bg-success/10"
              title="Advance"
              aria-label="Advance candidate"
            >
              <Check className="h-4 w-4" />
            </button>
          )}
          <button
            onClick={onReview}
            className="rounded p-1.5 text-on-surface-variant hover:bg-surface-variant"
            title="Review"
            aria-label="Review candidate"
          >
            <HelpCircle className="h-4 w-4" />
          </button>
          {canManage && app.stage !== 'rejected' && (
            <button
              onClick={onReject}
              className="rounded p-1.5 text-danger-text hover:bg-danger/10"
              title="Reject"
              aria-label="Reject candidate"
            >
              <X className="h-4 w-4" />
            </button>
          )}
          <button
            onClick={onReview}
            className="rounded p-1.5 text-on-surface-variant hover:bg-surface-variant"
            title="More"
            aria-label="More actions"
          >
            <MoreHorizontal className="h-4 w-4" />
          </button>
        </div>
      </td>
    </tr>
  );
}

// ─── New Job Modal ──────────────────────────────────────────────────────────────

interface NewJobModalProps {
  open: boolean;
  onClose: () => void;
  onCreate: (payload: {
    title: string;
    department: string;
    description: string;
    requiredSkills: string[];
    employmentType: EmploymentType;
    location: string;
    openings: number;
  }) => void;
  loading: boolean;
}

function NewJobModal({ open, onClose, onCreate, loading }: NewJobModalProps) {
  const [title, setTitle] = useState('');
  const [department, setDepartment] = useState('');
  const [description, setDescription] = useState('');
  const [skills, setSkills] = useState('');
  const [employmentType, setEmploymentType] = useState<EmploymentType>('full_time');
  const [location, setLocation] = useState('');
  const [openings, setOpenings] = useState('1');

  const { data: departments = [] } = useMasterDepartments();

  const handleSubmit = () => {
    if (!title.trim() || !department.trim()) return;
    onCreate({
      title: title.trim(),
      department: department.trim(),
      description: description.trim(),
      requiredSkills: skills
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean),
      employmentType,
      location: location.trim(),
      openings: Math.max(1, parseInt(openings, 10) || 1),
    });
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="New Job Posting"
      size="lg"
      footer={
        <div className="flex gap-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} loading={loading} disabled={!title.trim() || !department.trim()}>
            Create
          </Button>
        </div>
      }
    >
      <div className="grid gap-4">
        <Input label="Job Title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Senior Engineer" />
        <Select
          label="Department"
          options={departments.map((d) => ({ label: d.label, value: d.label }))}
          placeholder="Select a department"
          value={department}
          onChange={(e) => setDepartment(e.target.value)}
        />
        <div>
          <label className="mb-1.5 block text-body-sm font-medium text-on-surface">Description</label>
          <textarea
            className="h-24 w-full rounded border border-outline bg-surface px-3 py-2 text-data-md text-on-surface placeholder:text-on-surface-variant/70 focus:outline-none focus:ring-2 focus:ring-primary/40"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Describe the role..."
          />
        </div>
        <Input
          label="Required Skills (comma separated)"
          value={skills}
          onChange={(e) => setSkills(e.target.value)}
          placeholder="React, TypeScript, Node.js"
        />
        <div className="grid grid-cols-2 gap-4">
          <Select
            label="Employment Type"
            options={EMPLOYMENT_OPTIONS}
            value={employmentType}
            onChange={(e) => setEmploymentType(e.target.value as EmploymentType)}
          />
          <Input label="Location" value={location} onChange={(e) => setLocation(e.target.value)} placeholder="Remote" />
        </div>
        <Input
          label="Openings"
          type="number"
          value={openings}
          onChange={(e) => setOpenings(e.target.value)}
          placeholder="1"
        />
      </div>
    </Modal>
  );
}

// ─── New Candidate Modal ────────────────────────────────────────────────────────

interface NewCandidateModalProps {
  open: boolean;
  onClose: () => void;
  jobs: JobPosting[];
  defaultJobId: string | null;
  onAdd: (payload: {
    jobId: string;
    candidateName: string;
    email: string;
    phone: string;
    source: string;
    coverNote: string;
    resume: File | null;
  }) => void;
  loading: boolean;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function NewCandidateModal({ open, onClose, jobs, defaultJobId, onAdd, loading }: NewCandidateModalProps) {
  // Candidates can only be added to a job that is accepting applications, so
  // the picker only lists open roles (the server rejects non-open jobs too).
  const openJobs = useMemo(() => jobs.filter((j) => j.status === 'open'), [jobs]);
  const [jobId, setJobId] = useState<string>(() =>
    defaultJobId && openJobs.some((j) => j._id === defaultJobId) ? defaultJobId : openJobs[0]?._id ?? '',
  );
  const [candidateName, setCandidateName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [source, setSource] = useState('direct');
  const [coverNote, setCoverNote] = useState('');
  const [resume, setResume] = useState<File | null>(null);
  const [emailError, setEmailError] = useState('');
  const [fileError, setFileError] = useState('');

  const noOpenJobs = openJobs.length === 0;

  function handleFileChange(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0] ?? null;
    if (!file) {
      setResume(null);
      setFileError('');
      return;
    }
    const isPdf = file.type === 'application/pdf' || /\.pdf$/i.test(file.name);
    if (!isPdf) {
      setResume(null);
      setFileError('Only PDF resumes are accepted.');
      return;
    }
    if (file.size > MAX_RESUME_BYTES) {
      setResume(null);
      setFileError('Resume must be 10 MB or smaller.');
      return;
    }
    setFileError('');
    setResume(file);
  }

  const handleSubmit = () => {
    if (noOpenJobs || !jobId || !candidateName.trim()) return;
    const trimmedEmail = email.trim();
    if (trimmedEmail && !EMAIL_RE.test(trimmedEmail)) {
      setEmailError('Enter a valid email address.');
      return;
    }
    setEmailError('');
    onAdd({
      jobId,
      candidateName: candidateName.trim(),
      email: trimmedEmail,
      phone: phone.trim(),
      source,
      coverNote: coverNote.trim(),
      resume,
    });
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Add Candidate"
      size="md"
      footer={
        <div className="flex gap-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            loading={loading}
            disabled={noOpenJobs || !jobId || !candidateName.trim()}
          >
            Add
          </Button>
        </div>
      }
    >
      {noOpenJobs ? (
        <div className="rounded-md border border-outline bg-surface-variant/40 p-4 text-sm text-on-surface-variant">
          There are no open job postings yet. Open a role first — candidates can only be added to a job that is
          accepting applications.
        </div>
      ) : (
        <div className="grid gap-4">
          <Select
            label="Job Role"
            options={openJobs.map((j) => ({
              label: j.location ? `${j.title} — ${j.location}` : j.title,
              value: j._id,
            }))}
            value={jobId}
            onChange={(e) => setJobId(e.target.value)}
          />
          <Input label="Candidate Name" value={candidateName} onChange={(e) => setCandidateName(e.target.value)} placeholder="John Doe" />
          <Input
            label="Email"
            type="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              if (emailError) setEmailError('');
            }}
            placeholder="john@example.com"
            error={emailError || undefined}
          />
          <Input label="Phone" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+1 (555) 000-0000" />
          <Select
            label="Source"
            options={[
              { label: 'Direct', value: 'direct' },
              { label: 'Referral', value: 'referral' },
              { label: 'LinkedIn', value: 'linkedin' },
              { label: 'Naukri', value: 'naukri' },
              { label: 'Job Board', value: 'job_board' },
              { label: 'Other', value: 'other' },
            ]}
            value={source}
            onChange={(e) => setSource(e.target.value)}
          />
          <div>
            <label className="mb-1.5 block text-body-sm font-medium text-on-surface">Resume (PDF)</label>
            <input
              type="file"
              accept="application/pdf"
              onChange={handleFileChange}
              className="block w-full text-sm text-on-surface-variant file:mr-3 file:rounded file:border-0 file:bg-primary/10 file:px-3 file:py-2 file:text-sm file:font-medium file:text-primary hover:file:bg-primary/20"
            />
            {resume && !fileError && (
              <p className="mt-1.5 text-xs text-on-surface-variant">
                Selected: {resume.name} ({Math.round(resume.size / 1024)} KB)
              </p>
            )}
            {fileError && <p className="mt-1.5 text-xs text-danger-text">{fileError}</p>}
          </div>
          <div>
            <label className="mb-1.5 block text-body-sm font-medium text-on-surface">Cover Note (optional)</label>
            <textarea
              className="h-24 w-full rounded border border-outline bg-surface px-3 py-2 text-data-md text-on-surface placeholder:text-on-surface-variant/70 focus:outline-none focus:ring-2 focus:ring-primary/40"
              value={coverNote}
              onChange={(e) => setCoverNote(e.target.value)}
              placeholder="Any notes about this candidate..."
            />
          </div>
        </div>
      )}
    </Modal>
  );
}
